import React from "react";
import { Sheet, YStack, XStack, Text, View, Circle } from "tamagui";
import { RiCloseLine, RiPhoneLine } from "react-icons/ri";
import { colors } from "@styles/colors";
import { zIndexes } from "@utils/zIndexLevel";
import { isGasSkinV2, v2 } from "@screen/Home/pages/GasShop/skin";
import { toTelHref } from "@utils/func/supplierIdentity";
import TrackingMap from "./TrackingMap";
import type { DriverProfile } from "./types";
import type { TrackingDisplay } from "./trackingDisplay";
import type { LatLngPoint } from "./smoothing";

/**
 * 全螢幕追蹤（展開態）。
 *
 * 這裡才開放地圖手勢——收合卡片包在 ScrollView 裡不能吃拖曳，全螢幕沒有這個衝突。
 * 內容仍然只有兩個點：這一版不揭露配送路線與其他停靠點（那是別的客戶的住家）。
 *
 * 不用 SheetWrapper：那支把內容包在 ScrollView 裡，地圖需要固定滿版高度，兩者衝突。
 */

interface TrackingSheetProps {
  isOpen: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  driver: DriverProfile | null;
  /** 瓦斯行代表號（不是配送員門號，見 types.ts 的 DriverProfile） */
  contactPhone?: string | null;
  driverPoint: LatLngPoint | null;
  bearing?: number | null;
  ts?: number | null;
  destination: LatLngPoint | null;
  display: TrackingDisplay;
  showRouteHint: boolean;
}

const TrackingSheet: React.FC<TrackingSheetProps> = ({
  isOpen,
  onOpenChange,
  driver,
  contactPhone,
  driverPoint,
  bearing,
  ts,
  destination,
  display,
  showRouteHint,
}) => {
  const skinV2 = isGasSkinV2();

  return (
    <Sheet
      disableDrag
      open={isOpen}
      onOpenChange={onOpenChange}
      position={0}
      animation="medium"
      snapPoints={[100]}
      snapPointsMode="percent"
      modal
      zIndex={zIndexes.sheet}
      dismissOnOverlayPress={false}
      dismissOnSnapToBottom={false}>
      <Sheet.Overlay animation="lazy" enterStyle={{ opacity: 0 }} exitStyle={{ opacity: 0 }} />
      <Sheet.Frame bg={colors.light} h="100%" w="100%">
        <YStack f={1} position="relative">
          {/* 地圖吃滿剩餘空間；底部資訊面板浮在上面 */}
          <View f={1}>
            <TrackingMap
              driver={driverPoint}
              bearing={bearing}
              ts={ts}
              destination={destination}
              interactive
              faded={display.staleMinutes > 0}
              showRouteHint={showRouteHint}
              /* Sheet 關著的時候內容仍在樹上，不擋掉就是白跑一份動畫 */
              animate={isOpen}
              height="100%"
              /* 底部資訊面板浮在地圖上，marker 不能被它蓋住 */
              fitPadding={{ top: 72, bottom: 200, left: 40, right: 40 }}
            />
          </View>

          <View
            position="absolute"
            top={12}
            right={12}
            w={38}
            h={38}
            br={999}
            bg={colors.light}
            ai="center"
            jc="center"
            cursor="pointer"
            pressStyle={{ opacity: 0.7 }}
            shadowColor="rgba(0,0,0,0.25)"
            shadowRadius={6}
            /* 高於 Leaflet 的圖層 pane（最高 700），否則按鈕存在但看不見 */
            zIndex={800}
            onPress={() => onOpenChange(false)}>
            <RiCloseLine size={22} color={colors.text} />
          </View>

          {/* 底部資訊面板：狀態文案 + 配送員。與收合卡說同一句話，不另編文案。 */}
          <YStack
            bg={colors.light}
            btlr={24}
            btrr={24}
            px={20}
            pt={18}
            pb={28}
            gap={14}
            shadowColor="rgba(0,0,0,0.18)"
            shadowRadius={16}>
            <YStack gap={3}>
              <Text fontSize={17} fow="700" color={skinV2 ? v2.title : colors.text}>
                {display.headline}
              </Text>
              {display.sub ? (
                <Text fontSize={13} color={skinV2 ? v2.sub : colors.gray}>
                  {display.sub}
                </Text>
              ) : null}
            </YStack>

            {driver ? (
              <XStack
                ai="center"
                jc="space-between"
                gap={10}
                pt={12}
                borderTopWidth={1}
                borderTopColor={skinV2 ? v2.divider : colors.borderLight}>
                <XStack ai="center" gap={10} f={1} minWidth={0}>
                  <Circle size={36} bg={skinV2 ? v2.chipBg : colors.bgGray1} ai="center" jc="center" flexShrink={0}>
                    <Text fontSize={15} fow="700" color={skinV2 ? v2.chipText : colors.gray}>
                      {driver.name.slice(0, 1)}
                    </Text>
                  </Circle>
                  <YStack f={1} minWidth={0}>
                    <Text fontSize={14} fow="700" numberOfLines={1}>
                      {driver.name}
                    </Text>
                    <Text fontSize={12} color={skinV2 ? v2.sub : colors.gray}>
                      配送員
                    </Text>
                  </YStack>
                </XStack>
                {contactPhone ? (
                  <XStack
                    tag="a"
                    // tag="a" + href：撥號交給系統處理，不自己開視窗
                    href={toTelHref(contactPhone)}
                    ai="center"
                    gap={6}
                    px={14}
                    py={9}
                    br={999}
                    bg={colors.primaryActive}
                    flexShrink={0}
                    pressStyle={{ opacity: 0.75 }}
                    style={{ textDecoration: "none" }}>
                    <RiPhoneLine size={15} color={colors.primary} />
                    <Text fontSize={13} fow="700" color={colors.primary}>
                      聯絡瓦斯行
                    </Text>
                  </XStack>
                ) : null}
              </XStack>
            ) : null}
          </YStack>
        </YStack>
      </Sheet.Frame>
    </Sheet>
  );
};

export default TrackingSheet;
